import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { MeContext } from '../../../../../context/MeContext';

type WizardStep = 'scroll' | 'barcode' | 'revert' | 'olderDevices';

const stepKey = (accountId: string, step: WizardStep) =>
  `google_preview_wizard_${step}_seen_${accountId}`;

const wasStepSeen = (accountId: string, step: WizardStep): boolean => {
  try {
    return localStorage.getItem(stepKey(accountId, step)) === '1';
  } catch {
    return false;
  }
};

export const markStepSeen = (accountId: string, step: WizardStep): void => {
  try {
    localStorage.setItem(stepKey(accountId, step), '1');
  } catch {}
};

interface StepConfig {
  key: WizardStep;
  ref: React.RefObject<HTMLElement>;
  enabled: boolean;
  title: string;
  body: string;
  placement: 'left' | 'right' | 'top' | 'bottom';
  offsetX?: number;
  offsetY?: number;
}

const WizardPopover = ({
  anchorRef,
  title,
  body,
  onDismiss,
  placement = 'left',
  offsetX = 0,
  offsetY = 0,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  title: string;
  body: string;
  onDismiss: () => void;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  offsetX?: number;
  offsetY?: number;
}) => (
  <Popover isOpen placement={placement} autoFocus={false}>
    <PopoverTrigger>
      <Box
        position="absolute"
        ref={(el: HTMLDivElement | null) => {
          if (el && anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const parent = el.offsetParent?.getBoundingClientRect() ?? { top: 0, left: 0 };
            el.style.top = `${rect.top - parent.top + offsetY}px`;
            el.style.left = `${rect.left - parent.left + rect.width / 2 + offsetX}px`;
          }
        }}
        w="1px"
        h="1px"
        pointerEvents="none"
      />
    </PopoverTrigger>
    <PopoverContent
      bg="#1D2B36"
      borderRadius="10px"
      border="none"
      maxW="220px"
      boxShadow="0px 3px 3px rgba(0,0,0,0.1), 0px 10px 10px rgba(0,0,0,0.1), 0px 15px 15px rgba(0,0,0,0.15)"
      _focus={{ outline: 'none' }}
    >
      <PopoverArrow bg="#1D2B36" boxShadow="none" sx={{ '--popper-arrow-size': '12px' }} />
      <PopoverBody p={4}>
        <Flex direction="column" gap={2}>
          <Text textStyle="bodyFamilySemiBold" fontSize="14px" color="white" lineHeight="1.4">
            {title}
          </Text>
          <Text fontSize="12px" color="white" lineHeight="1.5">
            {body}
          </Text>
          <Button
            size="xs"
            alignSelf="flex-end"
            mt={1}
            px={3}
            py={1}
            h="30px"
            borderRadius="5px"
            variant="gradient"
            fontSize="12px"
            _hover={{ opacity: 0.9 }}
            onClick={onDismiss}
          >
            Got it
          </Button>
        </Flex>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

interface GooglePreviewWizardProps {
  scrollButtonRef: React.RefObject<HTMLElement>;
  barcodeButtonRef: React.RefObject<HTMLElement>;
  revertButtonRef: React.RefObject<HTMLElement>;
  showBarcodeStep: boolean;
  showRevertStep: boolean;
  showOlderDevicesStep?: boolean;
  showScrollStep?: boolean;
  scrolledToBottom?: boolean;
}

const GooglePreviewWizard = ({
  scrollButtonRef,
  barcodeButtonRef,
  revertButtonRef,
  showBarcodeStep,
  showRevertStep,
  showOlderDevicesStep = false,
  showScrollStep = true,
  scrolledToBottom,
}: GooglePreviewWizardProps) => {
  const meCtx = useContext(MeContext) as {
    state: { account?: { id?: string | number } | null };
  } | undefined;
  const accountId =
    meCtx?.state?.account?.id != null
      ? String(meCtx.state.account.id)
      : undefined;

  const steps: StepConfig[] = [
    {
      key: 'scroll',
      ref: scrollButtonRef,
      enabled: showScrollStep,
      title: 'Scroll for more',
      body: 'Your Google pass continues below. Scroll or tap the button to see it.',
      placement: 'top',
      offsetY: -5,
    },
    {
      key: 'barcode',
      ref: barcodeButtonRef,
      enabled: showBarcodeStep,
      title: 'Your QR code moved',
      body: 'With NFC and a barcode both enabled, the QR becomes a button in the top right once your pass is installed. Tap this icon to preview that screen.',
      placement: 'left',
      offsetX: -15,
      offsetY: 15,
    },
    {
      key: 'revert',
      ref: revertButtonRef,
      enabled: showRevertStep,
      title: 'Classic view',
      body: 'Switch between the new and classic Google Wallet preview.',
      placement: 'left',
      offsetX: -15,
      offsetY: 15,
    },
    {
      key: 'olderDevices',
      ref: revertButtonRef,
      enabled: showOlderDevicesStep,
      title: 'Older devices',
      body: "See how this pass appears on devices that don't support the latest design.",
      placement: 'left',
      offsetX: -15,
      offsetY: 15,
    },
  ];

  const [activeStep, setActiveStep] = useState<WizardStep | 'pending' | 'done'>('pending');

  const findNextUnseen = (afterIndex = -1): WizardStep | 'done' => {
    if (!accountId) return 'done';
    for (let i = afterIndex + 1; i < steps.length; i++) {
      const s = steps[i];
      if (s.enabled && s.ref.current && !wasStepSeen(accountId, s.key)) return s.key;
    }
    return 'done';
  };

  useEffect(() => {
    if (!accountId) return;
    const timer = setTimeout(() => setActiveStep(findNextUnseen()), 300);
    return () => clearTimeout(timer);
  }, [accountId, showBarcodeStep, showRevertStep, showOlderDevicesStep]);

  // Auto-dismiss the scroll step when the user scrolls to the bottom
  useEffect(() => {
    if (scrolledToBottom && activeStep === 'scroll' && accountId) {
      markStepSeen(accountId, 'scroll');
      const currentIndex = steps.findIndex((s) => s.key === 'scroll');
      setActiveStep(findNextUnseen(currentIndex));
    }
  }, [scrolledToBottom, activeStep, accountId]);

  if (activeStep === 'pending' || activeStep === 'done' || !accountId) return null;

  const currentConfig = steps.find((s) => s.key === activeStep);
  if (!currentConfig || !currentConfig.ref.current) return null;

  const handleDismiss = () => {
    markStepSeen(accountId, activeStep as WizardStep);
    const currentIndex = steps.findIndex((s) => s.key === activeStep);
    requestAnimationFrame(() => setActiveStep(findNextUnseen(currentIndex)));
  };

  return (
    <WizardPopover
      anchorRef={currentConfig.ref}
      title={currentConfig.title}
      body={currentConfig.body}
      onDismiss={handleDismiss}
      placement={currentConfig.placement}
      offsetX={currentConfig.offsetX}
      offsetY={currentConfig.offsetY}
    />
  );
};

export default GooglePreviewWizard;
